import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useNavigate } from "@tanstack/react-router";
import { toastManager } from "@salestrak/ui";

import {
  LeaveWorkspaceDocument,
  WorkspaceLeaveEligibilityDocument,
  connectionRows,
  type WorkspaceLeaveEligibilityRow,
} from "#app/lib/data";
import { useWorkspace, type Workspace } from "#app/lib/data/workspace-context";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { routeOptions } from "#app/lib/hooks/use-app-navigate";
import { AnalyticsContext, WorkspaceEvent, trackEvent } from "#app/lib/analytics";

/**
 * Leaving a workspace, from the viewer's side. The eligibility read answers
 * what the dialog shows before anyone presses anything; `leave` runs the
 * mutation and then walks the viewer out — off the workspace's routes first,
 * so nothing still mounted refetches against a membership that is gone, then
 * the provider drops the workspace, and the viewer lands in their one other
 * workspace when they have exactly one, or on the picker otherwise.
 */
export function useLeaveWorkspace(workspace: Pick<Workspace, "workspaceId" | "name">) {
  const navigate = useNavigate();
  const { detachWorkspace, switchWorkspace } = useWorkspace();
  const [leaving, setLeaving] = useState(false);

  // Fresh on every open: the answer changes with the roster (a second Owner
  // appointed in another tab), and a stale "you may leave" would only turn
  // into the mutation's own refusal a click later.
  const { data, loading } = useQuery(WorkspaceLeaveEligibilityDocument, {
    fetchPolicy: "network-only",
  });
  const eligibility = useMemo<WorkspaceLeaveEligibilityRow | null>(() => {
    const row = connectionRows(data?.workspaces).find(
      (w) => w.workspaceId === workspace.workspaceId,
    );
    return row?.viewerLeaveEligibility ?? null;
  }, [data, workspace.workspaceId]);

  const [leaveMutation] = useMutation(LeaveWorkspaceDocument);

  const leave = useCallback(async (): Promise<boolean> => {
    setLeaving(true);
    try {
      const { data } = await leaveMutation({ variables: { id: workspace.workspaceId } });
      const result = data?.leaveWorkspace;
      if (!result) return false;
      if (result.__typename === "ValidationError") {
        toastManager.add({ title: result.message, type: "error" });
        return false;
      }
      trackEvent({ context: AnalyticsContext.Workspace, event: WorkspaceEvent.Leave });
      toastManager.add({ title: `Left ${workspace.name}`, type: "success" });
      // Off the workspace's routes before the provider tears its state down.
      await navigate({ to: "/workspace-select", replace: true });
      let remaining: Workspace[] = [];
      try {
        remaining = await detachWorkspace(workspace.workspaceId);
      } catch {
        // The account list could not refresh; the picker refetches on its
        // own, and the membership is already gone server-side.
      }
      const active = remaining.filter((w) => w.memberStatus === "active");
      if (active.length === 1) {
        const [only] = active;
        if (only && (await switchWorkspace(only))) {
          await navigate(routeOptions(`/${only.urlPath}`, undefined, { replace: true }));
        }
      }
      return true;
    } catch (err) {
      toastMutationError(err, "Failed to leave workspace");
      return false;
    } finally {
      setLeaving(false);
    }
  }, [leaveMutation, workspace.workspaceId, workspace.name, navigate, detachWorkspace, switchWorkspace]);

  return { eligibility, eligibilityLoading: loading && !data, leave, leaving };
}
